import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  UserPlus, CheckCircle2, XCircle, AlertTriangle, Users, Trash2,
  MessageSquare, Send, Clock,
} from "lucide-react";
import { toast } from "sonner";

// Cast Approval Ledger — mirrors EpisodeSignoffLedger with cast-specific
// fields. PH submits substitution / new-addition requests; active desk
// members decide; only Programming Head seals `final_decision=approved`.
export default function CastApprovalLedger({ projectId, viewerRole = "ph_owner" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [submitOpen, setSubmitOpen] = useState(false);
  const [decisionCtx, setDecisionCtx] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get(`/projects/${projectId}/cast-approvals`);
      setData(resp);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, [projectId]);

  const withdraw = async (id) => {
    if (!confirm("Withdraw this cast approval request?")) return;
    try {
      await api.delete(`/projects/${projectId}/cast-approvals/${id}`);
      toast.success("Withdrawn");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not withdraw");
    }
  };

  const rows = (data?.cast_approvals || []).filter((r) => tab === "all" || r.status === tab);
  const counts = data?.counts || { pending: 0, approved: 0, rejected: 0,
                                    changes_requested: 0, total: 0 };
  const isPH = ["ph_owner", "admin"].includes(viewerRole);
  const canDecide = ["desk_member", "admin"].includes(viewerRole);

  return (
    <div className="cs-card rounded-2xl p-6" data-testid="cast-approval-panel">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-5">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
            Cast Approval Ledger
          </div>
          <h3 className="font-display text-2xl uppercase tracking-tight mt-1">
            Cast Changes · {counts.total}
          </h3>
          <div className="mt-1 text-xs text-white/55">
            {isPH
              ? "Submit substitutions / new additions for the channel to approve."
              : "Sign off cast changes so the PH knows who's cleared for shoot."}
          </div>
        </div>
        {isPH && (
          <Button onClick={() => setSubmitOpen(true)}
            className="btn-themed h-11 px-5 rounded-full text-xs uppercase tracking-[0.25em]"
            data-testid="cast-submit-btn">
            <UserPlus size={13} className="mr-1.5" /> Submit cast change
          </Button>
        )}
      </div>

      {/* Filter tabs (compact) */}
      <div className="flex items-center gap-1 border-b border-white/10 mb-4"
        data-testid="cast-tabs">
        {[
          { k: "pending",           label: `Pending (${counts.pending || 0})` },
          { k: "changes_requested", label: `Changes (${counts.changes_requested || 0})` },
          { k: "approved",          label: `Approved (${counts.approved || 0})` },
          { k: "rejected",          label: `Rejected (${counts.rejected || 0})` },
          { k: "all",               label: `All (${counts.total || 0})` },
        ].map(({ k, label }) => (
          <button key={k}
            onClick={() => setTab(k)}
            className={"px-3 py-2 text-[10px] tracking-[0.22em] uppercase border-b-2 -mb-px transition-colors " +
              (tab === k ? "border-[color:var(--association-theme)] text-white"
                         : "border-transparent text-white/45 hover:text-white/70")}
            data-testid={`cast-tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-white/45 py-10 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-white/45 py-10 text-center"
          data-testid="cast-empty">
          {tab === "pending"
            ? isPH ? "No pending cast changes." : "Nothing awaiting your decision."
            : `No ${tab.replace("_", " ")} cast changes.`}
        </div>
      ) : (
        <div className="space-y-3" data-testid="cast-list">
          {rows.map((r) => (
            <CastRow key={r.id} r={r}
              isPH={isPH} canDecide={canDecide}
              onApprove={() => setDecisionCtx({ row: r, action: "approve" })}
              onReject={() => setDecisionCtx({ row: r, action: "reject" })}
              onChanges={() => setDecisionCtx({ row: r, action: "request_changes" })}
              onWithdraw={() => withdraw(r.id)} />
          ))}
        </div>
      )}

      <SubmitDialog open={submitOpen} onOpenChange={setSubmitOpen}
        projectId={projectId} onSubmitted={load} />
      <DecisionDialog ctx={decisionCtx} onClose={() => setDecisionCtx(null)}
        projectId={projectId} onDecided={load} />
    </div>
  );
}

function CastRow({ r, isPH, canDecide, onApprove, onReject, onChanges, onWithdraw }) {
  const statusMap = {
    pending:           { bg: "bg-amber-500/15",   fg: "text-amber-300",   label: "Pending" },
    approved:          { bg: "bg-emerald-500/15", fg: "text-emerald-300", label: "Approved" },
    rejected:          { bg: "bg-red-500/15",     fg: "text-red-300",     label: "Rejected" },
    changes_requested: { bg: "bg-orange-500/15",  fg: "text-orange-300",  label: "Changes" },
  };
  const s = statusMap[r.status] || statusMap.pending;
  const decisions = r.decisions || [];
  const typeLabel = r.change_type?.replace(/_/g, " ") || "substitution";

  return (
    <div className="rounded-xl p-4 bg-white/[0.03] border border-white/8 hover:bg-white/[0.05] transition-colors"
      data-testid={`cast-row-${r.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-lg bg-white/8 flex items-center justify-center text-white/75 shrink-0">
          <Users size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-white truncate">
              {r.character_name}
            </span>
            <span className="text-[10px] tracking-[0.22em] uppercase text-white/45">
              {typeLabel}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.2em] ${s.bg} ${s.fg}`}
              data-testid={`cast-status-${r.status}`}>
              {s.label}
            </span>
          </div>
          <div className="mt-1 text-[12px] text-white/70">
            {r.previous_actor
              ? <>Was <b>{r.previous_actor}</b> · Proposed <b className="text-white">{r.proposed_actor}</b></>
              : <>Proposed <b className="text-white">{r.proposed_actor}</b></>}
          </div>
          <div className="mt-1 text-[11px] text-white/45">
            Submitted {r.submitted_at?.slice(0, 10)}
            {r.effective_from && ` · Effective ${r.effective_from}`}
            {r.effective_episodes && ` · ${r.effective_episodes}`}
          </div>
          {r.reason && (
            <div className="mt-2 text-[12px] text-white/60 italic line-clamp-2">
              &ldquo;{r.reason}&rdquo;
            </div>
          )}
          {r.media_urls?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {r.media_urls.slice(0, 3).map((u, i) => (
                <a key={i} href={u} target="_blank" rel="noopener noreferrer"
                  className="text-[10px] tracking-[0.22em] uppercase text-white/55 hover:text-white border border-white/10 rounded px-2 py-0.5"
                  data-testid={`cast-media-${r.id}-${i}`}>
                  Media {i + 1}
                </a>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {canDecide && r.status !== "approved" && (
            <>
              <Button onClick={onApprove}
                className="h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.22em] bg-emerald-500/80 hover:bg-emerald-500 text-white"
                data-testid={`cast-approve-${r.id}`}>
                <CheckCircle2 size={11} className="mr-1" /> Approve
              </Button>
              <Button onClick={onChanges} variant="outline"
                className="h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.22em] border-orange-500/50 text-orange-300 hover:bg-orange-500/10"
                data-testid={`cast-changes-${r.id}`}>
                Changes
              </Button>
              <Button onClick={onReject} variant="outline"
                className="h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.22em] border-red-500/50 text-red-300 hover:bg-red-500/10"
                data-testid={`cast-reject-${r.id}`}>
                Reject
              </Button>
            </>
          )}
          {isPH && r.status !== "approved" && (
            <button onClick={onWithdraw}
              className="text-white/40 hover:text-red-300 transition-colors p-1.5"
              data-testid={`cast-withdraw-${r.id}`}
              aria-label="Withdraw">
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>
      {decisions.length > 0 && (
        <div className="mt-3 pl-13 space-y-1.5">
          {decisions.slice(-3).reverse().map((d) => (
            <div key={d.id} className="text-[11px] text-white/60 flex items-start gap-2"
              data-testid={`cast-decision-row-${d.id}`}>
              <MessageSquare size={10} className="mt-0.5 shrink-0 text-white/40" />
              <div>
                <b className="text-white/80">{d.decided_by_name || "Channel exec"}</b>
                <span className="text-white/45"> · {d.member_role?.replace(/_/g, " ")} · </span>
                <span className={
                  d.decision === "approve" ? "text-emerald-300"
                  : d.decision === "reject" ? "text-red-300"
                  : "text-orange-300"
                }>
                  {d.decision.replace(/_/g, " ")}
                </span>
                {d.note && <span className="text-white/70"> — {d.note}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SubmitDialog({ open, onOpenChange, projectId, onSubmitted }) {
  const [character, setCharacter] = useState("");
  const [changeType, setChangeType] = useState("substitution");
  const [previous, setPrevious] = useState("");
  const [proposed, setProposed] = useState("");
  const [bio, setBio] = useState("");
  const [reason, setReason] = useState("");
  const [mediaRaw, setMediaRaw] = useState("");
  const [effFrom, setEffFrom] = useState("");
  const [effEps, setEffEps] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCharacter(""); setChangeType("substitution"); setPrevious("");
    setProposed(""); setBio(""); setReason(""); setMediaRaw("");
    setEffFrom(""); setEffEps("");
  };

  const submit = async () => {
    if (!character.trim() || !proposed.trim()) {
      toast.error("Character name and proposed actor are required");
      return;
    }
    setSaving(true);
    try {
      const media = mediaRaw.split("\n").map((s) => s.trim()).filter(Boolean);
      const body = {
        character_name:     character.trim(),
        change_type:        changeType,
        proposed_actor:     proposed.trim(),
      };
      if (previous.trim()) body.previous_actor = previous.trim();
      if (bio.trim())      body.proposed_actor_bio = bio.trim();
      if (reason.trim())   body.reason = reason.trim();
      if (media.length)    body.media_urls = media;
      if (effFrom)         body.effective_from = effFrom;
      if (effEps.trim())   body.effective_episodes = effEps.trim();
      await api.post(`/projects/${projectId}/cast-approvals`, body);
      toast.success("Cast change sent to the desk");
      onOpenChange(false); reset(); onSubmitted?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-[#0a0a0c] border border-white/10 text-white max-w-xl max-h-[92vh] overflow-y-auto"
        data-testid="cast-submit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-lg">
            Submit cast change
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            The channel programming team decides. Only a Programming Head&apos;s approval seals it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Change type</Label>
            <Select value={changeType} onValueChange={setChangeType}>
              <SelectTrigger className="mt-2 h-11 bg-black/40 border-white/15"
                data-testid="cast-type-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0c] border-white/15 text-white">
                <SelectItem value="substitution">Substitution</SelectItem>
                <SelectItem value="new_addition">New addition</SelectItem>
                <SelectItem value="extended_role">Extended role</SelectItem>
                <SelectItem value="guest_appearance">Guest appearance</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Character *</Label>
            <Input value={character} onChange={(e) => setCharacter(e.target.value)}
              placeholder="e.g. Anupamaa Shah"
              className="mt-2 h-11 bg-black/40 border-white/15"
              data-testid="cast-character-input" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {changeType === "substitution" && (
              <div>
                <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Previous actor</Label>
                <Input value={previous} onChange={(e) => setPrevious(e.target.value)}
                  className="mt-2 h-11 bg-black/40 border-white/15"
                  data-testid="cast-previous-input" />
              </div>
            )}
            <div className={changeType === "substitution" ? "" : "col-span-2"}>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Proposed actor *</Label>
              <Input value={proposed} onChange={(e) => setProposed(e.target.value)}
                className="mt-2 h-11 bg-black/40 border-white/15"
                data-testid="cast-proposed-input" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Actor bio (optional)</Label>
            <Textarea value={bio} onChange={(e) => setBio(e.target.value)}
              rows={3}
              placeholder="Prior credits, training, notable work."
              className="mt-2 bg-black/40 border-white/15 text-sm"
              data-testid="cast-bio-input" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Reason for change</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Health, scheduling, script direction, etc."
              className="mt-2 bg-black/40 border-white/15 text-sm"
              data-testid="cast-reason-input" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Media (headshots / showreel — one URL per line)
            </Label>
            <Textarea value={mediaRaw} onChange={(e) => setMediaRaw(e.target.value)}
              rows={3}
              placeholder="https://drive.google.com/…"
              className="mt-2 bg-black/40 border-white/15 text-sm font-mono"
              data-testid="cast-media-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Effective from</Label>
              <Input type="date" value={effFrom} onChange={(e) => setEffFrom(e.target.value)}
                className="mt-2 h-11 bg-black/40 border-white/15"
                data-testid="cast-effective-from-input" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Episode range</Label>
              <Input value={effEps} onChange={(e) => setEffEps(e.target.value)}
                placeholder="Ep 851 onwards"
                className="mt-2 h-11 bg-black/40 border-white/15"
                data-testid="cast-effective-episodes-input" />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => onOpenChange(false)}
              className="h-11 px-5 text-xs uppercase tracking-[0.22em]"
              data-testid="cast-submit-cancel">
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving || !character.trim() || !proposed.trim()}
              className="btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em]"
              data-testid="cast-submit-confirm">
              {saving ? "Sending…" : "Send for approval"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DecisionDialog({ ctx, onClose, projectId, onDecided }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setNote(""); }, [ctx]);
  if (!ctx) return null;

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/cast-approvals/${ctx.row.id}/decision`,
        { decision: ctx.action, note: note.trim() },
      );
      toast.success(
        data.status === "approved" ? "Approved & sealed"
        : data.status === "rejected" ? "Rejected"
        : data.status === "changes_requested" ? "Changes requested"
        : "Decision logged",
      );
      onClose(); onDecided?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not decide");
    } finally {
      setSaving(false);
    }
  };

  const label = ctx.action === "approve" ? "Approve"
    : ctx.action === "reject" ? "Reject"
    : "Request changes";

  return (
    <Dialog open={!!ctx} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0a0a0c] border border-white/10 text-white max-w-lg"
        data-testid="cast-decision-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-lg">
            {label} — {ctx.row.character_name}
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            {ctx.action === "approve"
              ? "Only a Programming Head's approval seals the cast change."
              : ctx.action === "reject"
              ? "Share why so the PH can regroup."
              : "List the specific changes you need to see."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Note {ctx.action === "approve" ? "(optional)" : "*"}
            </Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)}
              rows={4}
              className="mt-2 bg-black/40 border-white/15 text-sm"
              data-testid="cast-decision-note" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={onClose}
              className="h-11 px-5 text-xs uppercase tracking-[0.22em]"
              data-testid="cast-decision-cancel">
              Cancel
            </Button>
            <Button onClick={submit}
              disabled={saving || (ctx.action !== "approve" && !note.trim())}
              className={ctx.action === "approve"
                ? "btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em]"
                : "h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em] " +
                  (ctx.action === "reject"
                    ? "bg-red-500/80 hover:bg-red-500 text-white"
                    : "bg-orange-500/80 hover:bg-orange-500 text-white")}
              data-testid="cast-decision-submit">
              {saving ? "Saving…" : label}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
